import { DsButton, DsCheckbox, DsLabel } from '@frontends/ds';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { memo, type SyntheticEvent, useCallback, useEffect, useId, useMemo, useState } from 'react';
import 'react-phone-number-input/style.css';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import PhoneInputWithCountrySelect from 'react-phone-number-input/react-hook-form';
import { Link } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { showToast } from '@/components/Toast.tsx';
import useAuth from '@/hooks/useAuth.tsx';
import { useEntityConfig } from '@/hooks/useEntityConfig.tsx';
import axios from '@/lib/axios.ts';
import { registerSchema, RegisterSchemaType } from '@/types/Auth.ts';
import { CountryData, CurrentCountry } from '@/types/Country.ts';
import { IGenericError } from '@/types/Error.ts';
import CountrySelect from '../../components/CountrySelect.tsx';
import FormTextField from '../../components/FormTextField.tsx';
import Logo from '../../components/Logo.tsx';
import AuthFormWrapper from './AuthFormWrapper.tsx';

const stopPropagation = (e: SyntheticEvent) => e.stopPropagation();

const PHONE_INPUT_CLASSES =
  'h-9 rounded-md bg-backgroundField px-3 text-base/6 text-textPrimary outline-1 -outline-offset-1 outline-borderPrimary transition placeholder:text-textPlaceholder focus:outline-2 focus:-outline-offset-2 focus:outline-brandPrimary aria-invalid:outline-borderDangerPrimary';

const Register = memo(function Register() {
  const { t } = useTranslation();
  const { setToken, setClient, setOnboardingModalOpen } = useAuth();
  const [isView, setIsView] = useState(false);
  const phoneInputId = useId();
  const phoneErrorId = useId();
  const [defaultCountry, setDefaultCountry] = useState<string>();
  const config = useEntityConfig();
  const defaultEnvCountry = config.VITE_DEFAULT_REGISTRATION_COUNTRY;

  const clientCountryApi = useCallback(async () => {
    const response = await axios.get<CurrentCountry>('/countries/current');

    return response.data.country;
  }, []);

  const { data: clientCountry, isLoading: clientCountryLoading } = useQuery<string>({
    queryKey: ['client-country'],
    queryFn: clientCountryApi,
  });

  const fetchCountries = useCallback(async () => {
    const response = await axios.get<CountryData[]>('/countries');

    return response.data;
  }, []);

  const { data: countries = [], isLoading: countriesLoading } = useQuery<CountryData[]>({
    queryKey: ['countries'],
    queryFn: fetchCountries,
  });

  useEffect(() => {
    if (countries.length > 0) {
      const found = countries.find((country) => {
        return country.iso_3166_2 === (clientCountry || defaultEnvCountry);
      });

      if (found) setDefaultCountry(found.country_code.toString());
    }
  }, [clientCountry, countries, defaultEnvCountry]);

  const registerForm = useForm<RegisterSchemaType>({
    criteriaMode: 'all',
    resolver: zodResolver(registerSchema(t)),
    defaultValues: {
      email: '',
      phoneNumber: '',
      password: '',
      country_code: '',
      terms: false,
      notUSResidentOrTaxPayer: false,
    },
  });
  const phoneError = registerForm.formState.errors.phoneNumber?.message;

  // Set default country code
  useEffect(() => {
    registerForm.setValue('country_code', defaultCountry ?? '');
  }, [registerForm, defaultCountry]);

  const registerApi = useCallback(async (data: RegisterSchemaType) => {
    const response = await axios.post('/auth/register', data, {
      withCredentials: true,
      headers: {
        'X-Trace-ID': uuidv4(),
      },
    });

    return response.data;
  }, []);

  const registerMutation = useMutation({
    mutationKey: ['register'],
    mutationFn: registerApi,
    onSuccess: ({ client, accessToken }) => {
      setToken(accessToken);
      setOnboardingModalOpen(true);
      setClient(client);
      showToast({
        type: 'success',
        title: t('successful_login_title'),
        description: t('successful_login_desc', {
          name: client?.client_details?.name,
        }),
      });
    },
    onError: (e: AxiosError<IGenericError>) => {
      setToken(undefined);
      setClient(undefined);
      showToast({
        type: 'error',
        title: t('error_title'),
        description: t(e?.response?.data.context?.translationKey ?? '', e?.response?.data?.detail ?? e.message),
      });
    },
  });

  const registerOnSubmit = useCallback(
    (data: RegisterSchemaType, e?: React.BaseSyntheticEvent) => {
      e?.preventDefault();
      registerMutation.mutate(data);
    },
    [registerMutation],
  );

  const ConfirmationText = useMemo(() => {
    return (
      <Trans
        i18nKey="confirmationText"
        t={t}
        components={{
          customerAgreement: (
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a
              href={config.VITE_CUSTOMER_AGREEMENT}
              target="_blank"
              rel="noopener noreferrer"
              onPointerDown={stopPropagation}
              onClick={stopPropagation}
            />
          ),
          privacyPolicy: (
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a
              href={config.VITE_PRIVACY_POLICY}
              target="_blank"
              rel="noopener noreferrer"
              onPointerDown={stopPropagation}
              onClick={stopPropagation}
            />
          ),
        }}
      />
    );
  }, [config.VITE_CUSTOMER_AGREEMENT, config.VITE_PRIVACY_POLICY, t]);

  const HaveAccountText = useMemo(() => {
    return (
      <Trans
        i18nKey="have_an_account"
        t={t}
        components={{
          login: <Link to={{ pathname: '/login' }} className="hover:text-textBrand underline underline-offset-4" />,
        }}
      />
    );
  }, [t]);

  return (
    <AuthFormWrapper>
      <div className="flex w-full items-center justify-center">
        <div className="flex flex-col gap-6 w-full max-w-[348px] rounded-2xl border border-borderSurface bg-backgroundRaised py-6 text-textPrimary shadow-sm lg:border-none lg:shadow-none">
          <div className="flex justify-center items-center px-6">
            <Logo className="[zoom:1.25]" />
          </div>
          <div className="px-6">
            <form onSubmit={registerForm.handleSubmit(registerOnSubmit)}>
              <div className="flex flex-col gap-6 *:sm:w-[300px]">
                <div className="flex gap-2">
                  <div className="grid gap-2 w-full">
                    <DsLabel htmlFor={phoneInputId}>{t('phone')}</DsLabel>
                    <PhoneInputWithCountrySelect
                      control={registerForm.control}
                      name="phoneNumber"
                      id={phoneInputId}
                      placeholder={t('phone_placeholder')}
                      aria-invalid={!!phoneError}
                      aria-describedby={phoneError ? phoneErrorId : undefined}
                      numberInputProps={{ className: PHONE_INPUT_CLASSES }}
                    />
                    {phoneError && (
                      <p id={phoneErrorId} className="text-sm text-textDangerPrimary">
                        {phoneError}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <FormTextField
                    control={registerForm.control}
                    name="email"
                    id="email"
                    type="email"
                    label={t('email')}
                    placeholder="email@example.com"
                    isRequired
                  />
                </div>
                <div className="flex gap-2">
                  <Controller
                    rules={{ required: true }}
                    control={registerForm.control}
                    name="country_code"
                    render={({ field, fieldState }) => (
                      <div className="grid gap-2 max-w-[300px] w-full">
                        <CountrySelect
                          label={t('country')}
                          countries={countries}
                          isLoading={countriesLoading}
                          isDisabled={clientCountryLoading}
                          value={field.value}
                          onChange={field.onChange}
                          isInvalid={fieldState.invalid}
                          errorMessage={fieldState.error?.message}
                        />
                      </div>
                    )}
                  />
                </div>
                <div className="flex gap-2">
                  <FormTextField
                    control={registerForm.control}
                    name="password"
                    id="password"
                    type={isView ? 'text' : 'password'}
                    label={t('password')}
                    placeholder="********"
                    isRequired
                    buttonEnd={{
                      icon: isView ? 'eye' : 'eye-off',
                      'aria-label': t(isView ? 'hide_password' : 'show_password'),
                      onPress: () => setIsView(!isView),
                    }}
                  />
                </div>
                <div className="flex gap-2">
                  <Controller
                    control={registerForm.control}
                    name="terms"
                    rules={{ required: t('terms_required') }}
                    render={({ field, fieldState }) => (
                      <div className="grid gap-2 sm:w-[300px] w-full">
                        <DsCheckbox
                          size="sm"
                          validationBehavior="aria"
                          isSelected={field.value}
                          onChange={field.onChange}
                          isInvalid={fieldState.invalid}
                        >
                          <span className="text-textSecondary [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-textBrand">
                            {ConfirmationText}
                          </span>
                        </DsCheckbox>
                      </div>
                    )}
                  />
                </div>
                <div className="flex gap-2">
                  <Controller
                    control={registerForm.control}
                    name="notUSResidentOrTaxPayer"
                    rules={{
                      required: t('not_us_resident_or_tax_payer_required'),
                    }}
                    render={({ field, fieldState }) => (
                      <div className="grid gap-2 sm:w-[300px] w-full">
                        <DsCheckbox
                          size="sm"
                          validationBehavior="aria"
                          isSelected={field.value}
                          onChange={field.onChange}
                          isInvalid={fieldState.invalid}
                        >
                          {t('not_us_resident_or_tax_payer')}
                        </DsCheckbox>
                      </div>
                    )}
                  />
                </div>
                <div className="flex gap-2">
                  <div className="grid gap-2 sm:w-[300px] w-full">
                    <DsCheckbox size="sm">{t('marketing')}</DsCheckbox>
                  </div>
                </div>
                <DsButton type="submit" size="lg" className="w-full" isPending={registerMutation.isPending}>
                  {t('register')}
                </DsButton>
                <div className="flex gap-2 text-center text-base justify-center">
                  <span>{HaveAccountText}</span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AuthFormWrapper>
  );
});

export default Register;
